import React, { useState, useEffect, useMemo } from "react";
import { useDrag, useDrop } from "react-dnd";
import PteReadingExplanationButton from "./PteReadingExplanationButton";
import { getPteReadingBlankResultState } from "./pteReadingBlankResultUtils";
import {
  FillingWordsOuterDiv,
  FillingWordsMainDiv,
  FillingWordsParagraphText,
  FillingWordsMainDiv2,
  FillingWordsText,
  FillingWordsBlankBox,
  FillingWordsWordBox,
} from "./Style";

const resultAnswerStyle = (isCorrect) => ({
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  gap: "0.3rem",
  minWidth: "134px",
  minHeight: "39px",
  marginRight: "5px",
  marginBottom: "5px",
  padding: "0 0.55rem",
  boxSizing: "border-box",
  borderRadius: "5px",
  background: isCorrect ? "rgba(47, 158, 68, 0.08)" : "rgba(232, 53, 43, 0.08)",
  color: isCorrect ? "#239a48" : "#d93025",
  border: `1px solid ${isCorrect ? "rgba(35, 154, 72, 0.55)" : "rgba(217, 48, 37, 0.55)"}`,
  borderBottom: `2px solid ${isCorrect ? "#239a48" : "#d93025"}`,
  fontWeight: 400,
  lineHeight: "normal",
  verticalAlign: "middle",
});

const FillBlanksCard = ({
  textValue,
  fillingWords,
  setSelectedAnswers,
  isSubmitted = false,
  correctAnswers = [],
  explanationContext = null,
  testData = null,
  getBlankExplanationTarget = () => null,
}) => {
  const placeholderPattern = /#~#~#/g;
  
  const textSegments = useMemo(() => {
    if (!textValue) return [""];
    return textValue.split(placeholderPattern);
  }, [textValue]);
  
  const [availableFillingWords, setAvailableFillingWords] =
    useState(fillingWords);
  
  const numberOfBlanks = Math.max(0, textSegments.length - 1);
  
  const [filledWords, setFilledWords] = useState(() => 
    Array(numberOfBlanks).fill(null)
  );

  useEffect(() => {
    const newNumberOfBlanks = Math.max(0, textSegments.length - 1);
    setFilledWords(Array(newNumberOfBlanks).fill(null));
  }, [textValue, textSegments.length]);

  useEffect(() => {
    setAvailableFillingWords(fillingWords || []);
  }, [fillingWords]);

  useEffect(() => {
    const answers = filledWords.map(word => word === null ? "" : word);
    setSelectedAnswers(answers);
  }, [filledWords, setSelectedAnswers]);

  const moveWord = (word, fromIndex, toIndex) => {
    let newAvailableWords = [...availableFillingWords];
    let newFilledWords = [...filledWords];

    if (fromIndex === toIndex) {
      return;
    }

    if (fromIndex !== null && toIndex !== null) {
      if (newFilledWords[toIndex] && newFilledWords[toIndex] !== word) {
        newAvailableWords.push(newFilledWords[toIndex]);
      }
      newFilledWords[toIndex] = word;
      newFilledWords[fromIndex] = null;
    } else if (fromIndex !== null && toIndex === null) {
      newAvailableWords.push(word);
      newFilledWords[fromIndex] = null;
    } else if (fromIndex === null && toIndex !== null) {
      if (newFilledWords[toIndex]) {
        newAvailableWords.push(newFilledWords[toIndex]);
      }
      newFilledWords[toIndex] = word;
      newAvailableWords = newAvailableWords.filter((w) => w !== word);
    }

    setFilledWords(newFilledWords);
    setAvailableFillingWords(newAvailableWords);
  };

  const ResultBlank = ({ index }) => {
    const { answerText, isCorrect } = getPteReadingBlankResultState({
      selectedAnswer: filledWords[index],
      correctAnswer: correctAnswers[index],
    });
    const target = getBlankExplanationTarget(index);

    return (
      <span style={resultAnswerStyle(isCorrect)}>
        {answerText}
        {target && (
          <PteReadingExplanationButton
            explanationContext={explanationContext}
            testData={testData}
            target={target}
          />
        )}
      </span>
    );
  };

  const BlankBox = ({ index }) => {
    const [{ isOver }, drop] = useDrop({
      accept: "word",
      drop: (item, monitor) => moveWord(item.word, item.fromIndex, index),
      collect: (monitor) => ({
        isOver: !!monitor.isOver(),
      }),
    });

    const filledWord = filledWords[index];
    const [{ isDragging }, drag, preview] = useDrag({
      type: "word",
      item: { type: "word", word: filledWord, fromIndex: index },
      canDrag: () => !!filledWord,
      collect: (monitor) => ({
        isDragging: !!monitor.isDragging(),
      }),
      end: (item, monitor) => {
        if (!monitor.didDrop()) {
          moveWord(item.word, index, null);
        }
      },
    });

    const attachDrag = filledWord ? drag : (node) => node;
    const attachDrop = drop;

    return (
      <FillingWordsBlankBox
        ref={(node) => {
          attachDrag(node);
          attachDrop(node);
        }}
        style={{ opacity: isDragging ? 0.5 : 1 }}
        isDragging={isDragging}
      >
        {filledWord}
      </FillingWordsBlankBox>
    );
  };

  const WordBox = ({ word }) => {
    const [{ isDragging }, drag] = useDrag({
      type: "word",
      item: { word, fromIndex: null },
      end: (item, monitor) => {
        if (!monitor.didDrop()) {
          moveWord(item.word, null, null);
        }
      },
      collect: (monitor) => ({
        isDragging: !!monitor.isDragging(),
      }),
    });

    return (
      <FillingWordsWordBox ref={drag} style={{ opacity: isDragging ? 0.5 : 1 }}    isDragging={isDragging}>
        {word}
      </FillingWordsWordBox>
    );
  };

  return (
    <FillingWordsOuterDiv>
      <FillingWordsMainDiv>
        <FillingWordsParagraphText>
          {textSegments.map((segment, index) => (
            <React.Fragment key={index}>
              {segment}
              {index < textSegments.length - 1 &&
                (isSubmitted ? (
                  <ResultBlank index={index} />
                ) : (
                  <BlankBox index={index} />
                ))}
            </React.Fragment>
          ))}
        </FillingWordsParagraphText>
      </FillingWordsMainDiv>
      {!isSubmitted && (
        <FillingWordsMainDiv2>
          <FillingWordsText>
            {availableFillingWords.map((word, index) => (
              <WordBox key={index} word={word} />
            ))}
          </FillingWordsText>
        </FillingWordsMainDiv2>
      )}
    </FillingWordsOuterDiv>
  );
};

export default FillBlanksCard;
